import React from 'react';
import { Helmet } from 'react-helmet-async';
import { titleFor, headTags } from './socialTags';

/**
 * Every public route that carries share metadata. Written out rather than derived
 * from routeMeta.json because TypeScript cannot read literal types out of JSON, and
 * a typo'd path should fail to compile rather than throw while a page renders.
 * RouteSeo.test.tsx fails if this list and the JSON ever disagree.
 */
export const SEO_PATHS = [
  '/',
  '/features',
  '/features/assistant',
  '/fantasy-writing-software',
  '/compare',
  '/november',
  '/pricing',
  '/help',
  '/help/tutorial',
  '/help/support',
  '/tos',
  '/privacy',
] as const;

export type SeoPath = typeof SEO_PATHS[number];

/**
 * Title, description, canonical and social-card tags for one public page.
 *
 * The same tags are written into static HTML at build time by
 * scripts/prerender-meta.js, for crawlers that never run JavaScript. In a browser
 * removeStaticHead() deletes those before React renders, so these are the only copies
 * and client-side navigation never leaves a stale one behind. (#814)
 */
const RouteSeo: React.FC<{ path: SeoPath }> = ({ path }) => (
  <Helmet>
    <title>{titleFor(path)}</title>
    {headTags(path).map(({ tag, attrs }) =>
      tag === 'link'
        ? <link key={`${attrs.rel}`} {...attrs} />
        : <meta key={attrs.name || attrs.property} {...attrs} />,
    )}
  </Helmet>
);

export default RouteSeo;
